import { format, isValid, parseISO } from "date-fns";
import { currencyToNumber } from "helpers/utils";
import type { NovaContaPagarForm } from "../hooks/useCriarTituloPagar";
import type {
  ContaPagarView,
  CriarContaPagarPayload,
} from "../types/contas-pagar.types";

/** Converte valor monetário do form (centavos em dígitos ou PT-BR) para número. */
function parseMoeda(raw: string): number | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  const n = currencyToNumber(t);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function parseNumero(raw: string): number | undefined {
  const t = raw.trim().replace(/\s/g, "");
  if (!t) return undefined;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : undefined;
}

function valorParaCentavosString(valor: number): string {
  if (!Number.isFinite(valor) || valor <= 0) return "";
  return String(Math.round(valor * 100));
}

function isoParaYmd(iso: string | null | undefined): string {
  if (!iso?.trim()) return format(new Date(), "yyyy-MM-dd");
  const d = parseISO(iso);
  return isValid(d) ? format(d, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
}

export function tituloViewParaForm(row: ContaPagarView): NovaContaPagarForm {
  const desc =
    row.descricao.trim() === "—" ? "" : row.descricao.trim();
  return {
    descricao: desc,
    naturezaDespesa:
      row.naturezaDespesa === "FIXA" ? "FIXA" : "VARIAVEL",
    categoria: row.categoria,
    contexto: row.contexto,
    eventoId: row.eventoId,
    fornecedorId: row.fornecedorId ?? "",
    centroCustoId: row.centroCustoId ?? "",
    vencimento: isoParaYmd(row.dataVencimento),
    competenciaEm: isoParaYmd(row.competenciaEm ?? row.dataVencimento),
    valorTotal: valorParaCentavosString(row.valorTotal),
    observacao: row.observacao ?? "",
    baseCalculoImposto:
      row.baseCalculoImposto != null && row.baseCalculoImposto > 0
        ? valorParaCentavosString(row.baseCalculoImposto)
        : "",
    aliquotaImposto:
      row.aliquotaImposto != null && row.aliquotaImposto > 0
        ? String(row.aliquotaImposto)
        : "11",
  };
}

export function validarFormContaPagar(
  form: NovaContaPagarForm,
): { ok: true; payload: CriarContaPagarPayload } | { ok: false; error: string } {
  const descricao = form.descricao.trim();
  if (!descricao) {
    return { ok: false, error: "Informe a descrição da despesa." };
  }
  if (!form.vencimento.trim()) {
    return { ok: false, error: "Informe a data de vencimento." };
  }

  const isImposto = form.categoria === "IMPOSTOS";
  const payload: CriarContaPagarPayload = {
    descricao,
    vencimento: form.vencimento,
    competenciaEm: form.competenciaEm || form.vencimento,
    categoria: form.categoria,
    contexto: form.contexto,
    naturezaDespesa: form.naturezaDespesa,
    observacao: form.observacao.trim() || null,
    fornecedorId: form.fornecedorId.trim() || null,
    centroCustoId: form.centroCustoId.trim() || null,
    eventoId: form.contexto === "EVENTO" ? form.eventoId : null,
  };

  if (isImposto) {
    const base = parseMoeda(form.baseCalculoImposto);
    const aliquota = parseNumero(form.aliquotaImposto);
    if (base == null || base <= 0) {
      return { ok: false, error: "Informe a base de cálculo do imposto." };
    }
    if (aliquota == null || aliquota < 6 || aliquota > 13.2) {
      return { ok: false, error: "Alíquota deve estar entre 6% e 13,2%." };
    }
    payload.baseCalculoImposto = base;
    payload.aliquotaImposto = aliquota;
    const valorManual = parseMoeda(form.valorTotal);
    if (valorManual != null && valorManual > 0) {
      payload.valorTotal = valorManual;
    }
  } else {
    const valor = parseMoeda(form.valorTotal);
    if (valor == null || valor <= 0) {
      return { ok: false, error: "Informe o valor total da despesa." };
    }
    payload.valorTotal = valor;
  }

  return { ok: true, payload };
}
